/**
 * @fileoverview Searcher Hits Module.
 * Provides functionality to create a custom hits widget for displaying search results in InstantSearch.js.
 */

import {getNoResultsHtml} from "../Utils";

/**
 * Renders custom search hits into the specified container.
 * This function is used as a connector for a custom hits widget in InstantSearch.js.
 * It generates a list layout displaying search hits, each with an image, title, excerpt, and formatted date.
 *
 * @param {Object} renderOptions - The options and utilities provided by InstantSearch.js for rendering hits.
 * @param {string|null} type - The type of search hits, if any, to determine the URL format for hits.
 */
const SearcherHits = (renderOptions, type = null) => {
    const { hits, widgetParams } = renderOptions;
    if (hits.length === 0) {
        widgetParams.container.innerHTML = getNoResultsHtml();
    } else {
        widgetParams.container.innerHTML = `
        <div id="search-hits" class="hits">
            ${hits.map((hit) => `
                <a href="${type !== 'members' ? hit.permalink : hit.website}" target="${type !== 'members' ? '_self' : '_blank'}">
                    <div class="flex bg-snow min-h-tagged-post-mobile hover:bg-primary-pearl p-3">
                        <div class="min-w-tagged-post-mobile md:min-w-tagged-post md:min-h-tagged-post bg-center bg-cover bg-no-repeat" style="background-image: url(${hit.featured_image})">
                        </div>
                        <div class="p-2.5 md:py-4 md:px-6">
                            <div class="flex items-center mb-3 md:mb-6">
                                <div class="text-mobile-small md:text-body-regular w-fit px-3 py-1 text-white font-medium bg-primary-orange mr-6 capitalize">
                                ${getHitPostType(hit)}
                                </div>
                                <div class="text-mobile-small md:text-body-regular font-medium">
                                    ${showHitDateByType(hit)}
                                </div>
                            </div>
                            <div class="text-mobile-regular md:text-body-large font-medium text-sans leading-none md:leading-8">
                                ${hit.post_title ?? hit.title}
                            </div>
                        </div>
                    </div>
                </a>
            `).join('')}
        </div>
        `;
    }
};

function showHitDateByType(hit) {
    if (typesWithDate.includes(getHitPostType(hit))) {
        if (getHitPostType(hit) === 'event') {
            if (hit.start_date && hit.start_date != "0000-00-00 00:00:00")
                return `Event Date: ${formatDate(hit.start_date)}`;
        }
        return "";
    }
    return '';
}
/**
 * Formats a given date string into a more readable format.
 * Converts the date string to the format "MMM DD, YYYY" (e.g., "Oct 10, 2023").
 *
 * @param {string} inputDateStr - The date string to format.
 * @returns {string} A formatted date string.
 */
function formatDate(inputDateStr) {
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

    // Parse the input date string into a Date object
    const date = new Date(inputDateStr);

    // Extract the desired components
    const month = months[date.getMonth()];
    const day = date.getDate();
    const year = date.getFullYear();

    // Format and return the components in the desired format
    return `${month} ${day}, ${year}`;
}

const typesWithDate = [
    'news',
    'event',
    'resources'
]

function getHitPostType(hit) {
    const { post_type } = hit;
    const postTypeMap = {
        global_network: 'global network',
        resource: 'resources'
    };
    return postTypeMap[post_type] ?? post_type ?? 'news';
}

export default SearcherHits;